<template>
  <div class="py-8">
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div v-for="category in categories" :key="category.id" class="group relative">
        <router-link :to="category.id === 'all_categories' ? '/category' : `/category/${category.slug}`" class="block">
          <div 
            class="flex flex-col items-center p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
            :class="[
              category.id === 'all_categories' 
                ? 'relative shadow-md hover:shadow-lg border-2 border-orange-400 dark:border-orange-700' 
                : 'bg-white dark:bg-gray-800'
            ]"
            :style="category.id === 'all_categories' ? 'background-color: #ED8132; border-color: #ED8132;' : ''"
          >
            <div 
              class="w-16 h-16 mb-3 flex items-center justify-center" 
              :class="[
                category.id === 'all_categories'
                  ? 'text-white transform transition-all duration-500 hover:scale-110 hover:rotate-3'
                  : 'text-secondary-500 dark:text-secondary-400'
              ]"
            >
              <FontAwesomeIcon :icon="category.faIcon" size="2x" :class="[category.id === 'all_categories' ? 'text-3xl drop-shadow-lg' : 'text-3xl']" />
            </div>
            <span 
              class="text-center text-sm font-medium"
              :class="[
                { 'font-[Vazirmatn]': isTextRTL(category.name.value) },
                category.id === 'all_categories'
                  ? 'text-white group-hover:text-orange-50 font-semibold'
                  : 'text-gray-700 dark:text-gray-300 group-hover:text-secondary-600 dark:group-hover:text-secondary-400'
              ]"
            >
              {{ category.name.value }}
            </span>
          </div>
          
          <!-- Category Description Tooltip -->
          <div 
            class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
            @mouseenter="showTooltip(category.id)" 
            @mouseleave="hideTooltip"
          >
            <div 
              v-show="activeTooltip === category.id"
              class="absolute top-full left-0 right-0 mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm"
              :class="{ 'font-[Vazirmatn] text-right': isTextRTL(category.description.value) }"
            >
              <div class="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 rotate-45 bg-white dark:bg-gray-800 border-t border-l border-gray-200 dark:border-gray-700"></div>
              <p class="text-gray-700 dark:text-gray-300">{{ category.description.value }}</p>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { isRTL } from '@/utils/text'
import { useTranslation } from '@/composables/useTranslation'
import { FontAwesomeIcon } from '@/utils/fontawesome'

const { t } = useTranslation()
const activeTooltip = ref<string | null>(null)

const showTooltip = (categoryId: string) => {
  activeTooltip.value = categoryId
}

const hideTooltip = () => {
  activeTooltip.value = null
}

const categories = [
  {
    id: 'real_estate',
    slug: 'real-estate',
    name: computed(() => t('listings.categories.real_estate')),
    description: computed(() => t('listings.category_descriptions.real_estate')),
    icon: 'real_estate_agent',
    faIcon: 'city'
  },
  {
    id: 'vehicles',
    slug: 'vehicles',
    name: computed(() => t('listings.categories.vehicles')),
    description: computed(() => t('listings.category_descriptions.vehicles')),
    icon: 'transportation',
    faIcon: 'truck-plane'
  },
  {
    id: 'electronics',
    slug: 'electronics',
    name: computed(() => t('listings.categories.electronics')),
    description: computed(() => t('listings.category_descriptions.electronics')),
    icon: 'devices_other',
    faIcon: 'laptop'
  },
  {
    id: 'fashion',
    slug: 'fashion',
    name: computed(() => t('listings.categories.fashion')),
    description: computed(() => t('listings.category_descriptions.fashion')),
    icon: 'shopping_bag',
    faIcon: 'tshirt'
  },
  {
    id: 'business',
    slug: 'services',
    name: computed(() => t('listings.categories.business')),
    description: computed(() => t('listings.category_descriptions.business')),
    icon: 'home_repair_service',
    faIcon: 'wrench'
  },
  {
    id: 'pets',
    slug: 'pets',
    name: computed(() => t('listings.categories.pets')),
    description: computed(() => t('listings.category_descriptions.pets')),
    icon: 'pets',
    faIcon: 'paw'
  },
  {
    id: 'personal',
    slug: 'personal',
    name: computed(() => t('listings.categories.personal')),
    description: computed(() => t('listings.category_descriptions.personal')),
    icon: 'group',
    faIcon: 'calendar-alt'
  },
  {
    id: 'all_categories',
    slug: 'all-categories',
    name: computed(() => t('listings.categories.allcategories')),
    description: computed(() => t('listings.category_descriptions.allcategories')),
    icon: 'apps',
    faIcon: 'store'
  }
]

// Computed property to check if text is RTL
const isTextRTL = computed(() => {
  return (text: string) => isRTL(text)
})
</script>

<style scoped>
/* Add any additional styles here */
</style>
